import { useTranslations } from '@vocab/react';
import {
  Card,
  Columns,
  Column,
  Stack,
  Text,
  ButtonIcon,
  IconEdit,
  Drawer,
  useToast,
  Box,
} from 'braid-design-system';
import React, { useEffect, useState } from 'react';
import translations from './.vocab';

import FormContainer from 'src/components/Account/ChangeEmail/Form/FormContainer';
import analytics from 'src/modules/analytics';
import { useTypedSelector } from 'src/reducers';
import type { PageAnalytics } from 'src/reducers/page';
import type { UserDetails } from 'src/reducers/user';
import { PageTitles } from 'src/components/Notifications/pageTitle';
import { LoadingItem } from 'src/components/LoadingItem/LoadingItem';

interface Props {
  pageAnalytics: PageAnalytics;
  userDetails: UserDetails | undefined;
}

export const ChangeEmail = ({ pageAnalytics, userDetails }: Props) => {
  const { t } = useTranslations(translations);

  const email = useTypedSelector((state) => state.user.emailAddress);

  const [openingEventTriggered, setOpeningEventTriggered] = useState(false);
  useEffect(() => {
    if (openingEventTriggered) {
      const { trackLink } = analytics({});
      trackLink({
        eventName: 'settings_section_displayed',
        currentPageSection: 'change email',
        currentPage: 'account settings',
        ...pageAnalytics,
        ...userDetails,
      });
    }
  }, [openingEventTriggered, pageAnalytics, userDetails]);

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [success, setSuccess] = useState(false);
  const toggle = () => {
    const wasClosed = drawerOpened === false;
    if (wasClosed) {
      setOpeningEventTriggered(true);
      setSuccess(false);
    }
    setDrawerOpened((currentState) => !currentState);
  };

  const showToast = useToast();
  useEffect(() => {
    if (success === true) {
      showToast({
        key: 'changeEmailSuccessToast',
        tone: 'positive',
        message: t('All Done! Your email has been updated.'),
      });
    }
  }, [showToast, success, t]);

  return (
    <Box data-testid="change-email-card">
      <Card rounded>
        <Columns space="small">
          <Column>
            <Stack space="medium">
              <Text weight="strong">{t('Email')}</Text>
              {Boolean(email) ? (
                <Text tone="secondary">{email}</Text>
              ) : (
                <LoadingItem size="standard" width="small" />
              )}
            </Stack>
          </Column>
          <Column width="content">
            <ButtonIcon
              id="changeEmailButton"
              variant="transparent"
              icon={<IconEdit />}
              label={t('Change email')}
              onClick={toggle}
            />
          </Column>
        </Columns>
      </Card>
      <Drawer
        id="changeEmailDrawer"
        title={t('Change email')}
        open={drawerOpened === true}
        onClose={toggle}
        width="small"
      >
        <Text>
          {t(
            'Email is used to sign in to BRAND and to be contacted by employers.',
            {
              brand: PageTitles[pageAnalytics.brand],
            },
          )}
        </Text>

        <FormContainer toggle={toggle} setSuccess={setSuccess} />
      </Drawer>
    </Box>
  );
};
